import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./FindJobs.module.css";
import dummyJobs from "../../../data/jobs";
import {
  FiSearch, FiMapPin, FiBookmark, FiFilter,
  FiClock, FiBriefcase, FiDollarSign, FiChevronDown
} from "react-icons/fi";

const jobTypes = ["Full Time", "Part Time", "Contract", "Internship"];
const workModes = ["On-site", "Remote", "Hybrid"];
const salaryRanges = ["Less than ₹30k", "₹30k – ₹60k", "₹60k – ₹1L", "₹1L+"];
const experienceLevels = ["0-1 years", "1-3 years", "3-5 years", "5+ years"];

const FindJobs = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [location, setLocation] = useState("");
  const [savedJobs, setSavedJobs] = useState(new Set());
  const [showFilters, setShowFilters] = useState(true);
  const [sortBy, setSortBy] = useState("Newest");
  const [activeFilters, setActiveFilters] = useState({
    type: [],
    mode: [],
    salary: [],
    experience: [],
  });

  const toggleFilter = (category, value) => {
    setActiveFilters((prev) => {
      const current = prev[category];
      return {
        ...prev,
        [category]: current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });
  };

  const toggleSave = (e, id) => {
    e.stopPropagation();
    setSavedJobs((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const clearFilters = () => {
    setActiveFilters({ type: [], mode: [], salary: [], experience: [] });
  };

  const totalActiveFilters = Object.values(activeFilters).flat().length;

  const filteredJobs = useMemo(() => {
    let jobs = dummyJobs.filter((job) => {
      const matchSearch =
        !searchTerm ||
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.tags.some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchLocation =
        !location || job.location.toLowerCase().includes(location.toLowerCase());
      const matchType =
        activeFilters.type.length === 0 || activeFilters.type.includes(job.type);
      const matchMode =
        activeFilters.mode.length === 0 || activeFilters.mode.includes(job.mode);
      return matchSearch && matchLocation && matchType && matchMode;
    });

    if (sortBy === "Newest") jobs = [...jobs].sort((a, b) => a.postedDays - b.postedDays);
    if (sortBy === "Oldest") jobs = [...jobs].sort((a, b) => b.postedDays - a.postedDays);

    return jobs;
  }, [searchTerm, location, activeFilters, sortBy]);

  const handleJobClick = (job) => {
    navigate(`/job-desc/${job.id}`, { state: { job } });
  };

  const FilterGroup = ({ title, category, options }) => (
    <div className={styles.filterGroup}>
      <h4 className={styles.filterGroupTitle}>{title}</h4>
      {options.map((opt) => (
        <label key={opt} className={styles.filterOption}>
          <input
            type="checkbox"
            checked={activeFilters[category].includes(opt)}
            onChange={() => toggleFilter(category, opt)}
            className={styles.filterCheckbox}
          />
          <span className={`${styles.filterLabel} ${activeFilters[category].includes(opt) ? styles.filterLabelActive : ""}`}>
            {opt}
          </span>
        </label>
      ))}
    </div>
  );

  return (
    <div className={styles.page}>
      {/* Top Search Section */}
      <div className={styles.topSection}>
        <div className={styles.topInner}>
          <h1 className={styles.pageTitle}>Find Your Dream Job</h1>
          <p className={styles.pageSubtitle}>Browse {dummyJobs.length}+ curated opportunities from top companies</p>
          <div className={styles.searchBar}>
            <div className={styles.searchInput}>
              <FiSearch className={styles.searchIcon} />
              <input
                placeholder="Job title, skills, or company"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles.searchDivider} />
            <div className={styles.searchInput}>
              <FiMapPin className={styles.searchIcon} />
              <input
                placeholder="City or Remote"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>
            <button className={styles.searchBtn}>Search</button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Sidebar Filters */}
        {showFilters && (
          <aside className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
              <h3 className={styles.sidebarTitle}>
                <FiFilter /> Filters
              </h3>
              {totalActiveFilters > 0 && (
                <button className={styles.clearBtn} onClick={clearFilters}>
                  Clear all ({totalActiveFilters})
                </button>
              )}
            </div>
            <FilterGroup title="Job Type" category="type" options={jobTypes} />
            <FilterGroup title="Work Mode" category="mode" options={workModes} />
            <FilterGroup title="Salary Range" category="salary" options={salaryRanges} />
            <FilterGroup title="Experience" category="experience" options={experienceLevels} />
          </aside>
        )}

        {/* Job Listings */}
        <div className={styles.listings}>
          <div className={styles.listingsHeader}>
            <div className={styles.resultsCount}>
              <button
                className={`${styles.filterToggleBtn} ${showFilters ? styles.filterToggleActive : ""}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FiFilter /> {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
              <span className={styles.resultText}>
                {filteredJobs.length} job{filteredJobs.length !== 1 ? "s" : ""} found
              </span>
            </div>
            <div className={styles.sortWrapper}>
              <span className={styles.sortLabel}>Sort by:</span>
              <div className={styles.sortSelect}>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option>Newest</option>
                  <option>Oldest</option>
                </select>
                <FiChevronDown className={styles.sortChevron} />
              </div>
            </div>
          </div>

          <div className={styles.jobList}>
            {filteredJobs.length === 0 ? (
              <div className={styles.emptyState}>
                <FiBriefcase className={styles.emptyIcon} />
                <h3>No jobs found</h3>
                <p>Try adjusting your search or filters</p>
                <button className={styles.clearFiltersBtn} onClick={() => { setSearchTerm(""); setLocation(""); clearFilters(); }}>
                  Clear all filters
                </button>
              </div>
            ) : (
              filteredJobs.map((job) => (
                <div
                  key={job.id}
                  className={styles.jobCard}
                  onClick={() => handleJobClick(job)}
                >
                  {job.featured && <span className={styles.featuredBadge}>Featured</span>}
                  <div className={styles.jobCardTop}>
                    <div
                      className={styles.jobLogo}
                      style={{ background: job.companyColor + "18", color: job.companyColor }}
                    >
                      {job.companyInitial}
                    </div>
                    <div className={styles.jobInfo}>
                      <h3 className={styles.jobTitle}>{job.title}</h3>
                      <div className={styles.jobMeta}>
                        <span className={styles.jobCompany}>{job.company}</span>
                        <span className={styles.metaDot}>·</span>
                        <span className={styles.jobLocation}>
                          <FiMapPin size={13} /> {job.location}
                        </span>
                      </div>
                    </div>
                    <button
                      className={`${styles.saveBtn} ${savedJobs.has(job.id) ? styles.saveBtnActive : ""}`}
                      onClick={(e) => toggleSave(e, job.id)}
                      title="Save job"
                    >
                      <FiBookmark />
                    </button>
                  </div>

                  <div className={styles.jobCardBottom}>
                    <div className={styles.jobTags}>
                      <span className={`${styles.jobTag} ${styles.tagType}`}>{job.type}</span>
                      <span className={`${styles.jobTag} ${styles.tagMode}`}>{job.mode}</span>
                      {job.tags.map((tag) => (
                        <span key={tag} className={styles.jobTag}>{tag}</span>
                      ))}
                    </div>
                    <div className={styles.jobRight}>
                      <span className={styles.salary}>
                        <FiDollarSign size={13} /> {job.salary}
                      </span>
                      <span className={styles.postedTime}>
                        <FiClock size={13} />
                        {job.postedDays === 0 ? "Today" : job.postedDays === 1 ? "1 day ago" : `${job.postedDays} days ago`}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindJobs;
